import { Injectable, Input } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { BehaviorSubject, iif, interval, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { ModalConfirm } from '../../../shared/models/modal-confirm.model';
import { AppState } from '../../../store/app-reducers';
import { ModalConfirmPage } from '../components/modal-confirm/modal-confirm.page';
import { ZoomImagePage } from '../components/zoom-image/zoom-image.page';
import { IFechasEjecucion } from '../models/menu-item';
import { IConfigCamera, IEjecucionPalanca, IToast, TOAST_EJECUCION } from '../models/task-ejecucion';
import { StorageData } from './../../../shared/services/utilities/storage-data.class';

@Injectable({
  providedIn: 'root'
})
export class TransferDataService {

  @Input() disableBack = false;
  @Input() ejecucionTareas: IEjecucionPalanca[] = [];
  @Input() toastEjecucion: IToast = TOAST_EJECUCION;
  @Input() configuracionesC: IConfigCamera = {};

  public temporizador$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private openModal: HTMLIonModalElement | null = null
  private confirmModal: HTMLIonModalElement | null = null
  private alert: HTMLIonAlertElement | null = null

  constructor(
    private modalController: ModalController
    , private alertController: AlertController
    , private store: Store<AppState>
  ) { }

  public getDataStorage = async (key: string): Promise<any> => await StorageData.getDataStorage(key)

  public emitTemporizador = (emit: boolean) => this.temporizador$.next(emit);

  public temporizador = (): Observable<{ time: number; minutes: string; }> => {
    return this.temporizador$
      .pipe(
        switchMap(exe => this.store.select('userData').pipe(
          map(value => value.Ejecucion.FechasEjecucion || []),
          filter(f => f.length > 0),
          map(f => ({ exe, ...f[0] })),
          distinctUntilChanged((old, crt) => old.HorarioEjecucion === crt.HorarioEjecucion && old.FechaEjecucionFin === crt.FechaEjecucionFin),
        )
        ),
        map(data => Object.keys(data).length > 1 ? data : ({ ...data, exe: false })),
        switchMap(fechas => iif(
          () => !fechas.exe, of(({ time: 0, minutes: '0m' })).pipe(take(1)),
          interval(5000).pipe(
            startWith(0),
            map(() => this.RestanteMinutos(fechas.FechaEjecucionFin)),
            filter(time => time > 0),
            distinctUntilChanged(),
            map(time => ({ time, minutes: this.formatTime(time) })),
            takeUntil(this.temporizador$.pipe(skip(1)))
          )
        )
        ),
      )
  }

  public RestanteMinutos = (fechaFin: string): number => {
    const momentoAhora = moment();
    const horarioFin = moment(fechaFin);
    return horarioFin.diff(momentoAhora, 'minutes') + 1;
  }

  public betweenEjecucionFechas = (fechas: IEjecucionPalanca[]): number => {
    let fechaFin = 0;
    fechas.forEach(element => {
      if (moment().isBetween(element.HorarioEjecucion, element.FechaEjecucionFin)) {
        fechaFin = this.RestanteMinutos(element.FechaEjecucionFin || '');
      } else {
        fechaFin = 0;
      }
    });
    return fechaFin;
  }

  public betweenEjecucion = (fechas: IFechasEjecucion[]): boolean => {
    const between: boolean[] = []
    fechas.forEach(element => {
      between.push(moment().isBetween(element.HorarioEjecucion, element.FechaEjecucionFin))
    })

    const returnBoleean = between.filter(x => x).length > 0 ? true : false

    return returnBoleean
  }

  public betweenEjecucionTiempo = (fechas: IFechasEjecucion[]): number => {
    let fechaFin = 0;
    fechas.forEach(element => {
      if (moment().isBetween(element.HorarioEjecucion, element.FechaEjecucionFin)) {
        fechaFin = this.RestanteMinutos(element.FechaEjecucionFin);
      } else {
        fechaFin = 0;
      }
    });
    return fechaFin;
  }

  public betweenEjecucionFinish = (fechas: IFechasEjecucion[]): boolean => fechas.some(fecha => moment().isBetween(fecha.HorarioEjecucion, fecha.FechaEjecucionFin));

  public FechaEnTarea = (fechas: IFechasEjecucion[]): IFechasEjecucion => fechas.filter(fechas => moment().isBetween(fechas.HorarioEjecucion, fechas.FechaEjecucionFin))[0];

  public formatTime = (time: number): string => {
    const days = Math.floor((time * 60000) / (1000 * 60 * 60 * 24));
    const hours = Math.floor(((time * 60000) % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor(((time * 60000) % (1000 * 60 * 60)) / (1000 * 60));
    return (days > 0 ? `${days}d ` : '') + (hours > 0 ? `${hours}h ` : '') + `${minutes}m`;
  }

  public onZoom = async (image: string) => {
    this.openModal = await this.modalController.create({
      component: ZoomImagePage
      , mode: 'md'
      , backdropDismiss: true
      , cssClass: 'ion-modal-zoom'
      , componentProps: { Imagen: image }
    });

    await this.openModal.present();
    const { data } = await this.openModal.onDidDismiss();
    return data;
  }

  public onZoomDismiss() {
    if (this.openModal !== null) {
      this.openModal.dismiss();
    }
  }

  public async activateCameraOverlay(path: string): Promise<boolean> {
    const dataOverly = await this.getDataStorage(path);
    if (dataOverly) {
      return dataOverly.firstTime === 1
    } else {
      return true
    }
  }

  public async modalConfirm(modalConfirm: ModalConfirm) {
    this.confirmModal = await this.modalController.create({
      component: ModalConfirmPage
      , componentProps: { ModalConfirm: modalConfirm }
      , cssClass: 'modal-confirm'
      , backdropDismiss: false
      , mode: 'md'
    });

    await this.confirmModal.present();
    const { data } = await this.confirmModal.onDidDismiss();
    return data;
  }

  public async modalConfirmDismiss() {
    if (this.confirmModal !== null) {
      await this.confirmModal.dismiss(false);
    }
  }

  public async presentAlert(message: string) {
    this.alert = await this.alertController.create({
      message,
      cssClass: 'ion-alert-generic-message',
      backdropDismiss: false,
      buttons: [{
        text: 'ACEPTAR',
        cssClass: 'secondary',
        handler: () => { }
      }],
      mode: 'md'
    });

    await this.alert.present();
  }

  public async alertDismiss() {
    if (this.alert !== null) {
      this.alert.dismiss()
    }
  }

  public async presentAlert2(message: string) {
    this.alert = await this.alertController.create({
      message,
      buttons: [{
        text: 'Aceptar',
        cssClass: 'secondary',
        handler: () => true
      }],
      mode: 'ios',
      backdropDismiss: false,
    })

    await this.alert.present()
    const { data } = await this.alert.onDidDismiss()

    return data
  }

  /**
   * Accese a la propiedad y retorna el valor total de la suma
   * @param array el paquete acumulado de los objetos
   * @param propiedad a acceder para reducir los valores
   * @returns el valor total de la suma
   */
  public sumTotal = (array: Array<any>, property: string): number => array.map(p => p[property]).reduce((a, b) => a + b, 0);


  /**
   * Convierte un string Base64 (con o sin prefijo data:) en un objeto Blob.
   */
  public base64ToBlob(base64Data: string, contentType: string = 'image/jpeg'): Blob {
    const base64Clean = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;

    const byteCharacters = atob(base64Clean);
    const u8Array = new Uint8Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      u8Array[i] = byteCharacters.charCodeAt(i);
    }

    return new Blob([u8Array], { type: contentType });
  }

}
